import React, { useCallback, useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
// `useNoticeTemplate` is an API call, not a React hook; the alias keeps the hooks lint rule honest.
import { createNoticeTemplate, deleteNoticeTemplate, listNoticeTemplates, useNoticeTemplate as applyNoticeTemplate } from "../noticesApi";

/**
 * The same ten notices go out every year — fire alarm test, water shut-off, snow removal,
 * parking sweep. Saving them once turns a five-minute job into twenty seconds and, more
 * usefully, makes every one of them read the same whoever is on shift.
 *
 * Templates carry words and settings only. A file belongs to one event on one date.
 */
const TemplatePicker = ({ draft, onApply, canDelete }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    // Defensive: a caller without the templates API (an older mock, a stubbed module) should
    // cost the compose screen nothing.
    if (typeof listNoticeTemplates !== "function") return;
    Promise.resolve()
      .then(() => listNoticeTemplates())
      .then((res) => setItems(res?.data || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(load, [load]);

  const worthSaving = !!String(draft.title || "").trim() || !!String(draft.message || "").trim();

  const apply = async (t) => {
    setBusy(true);
    try {
      const res = await applyNoticeTemplate(t._id);
      if (res?.data) onApply(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not open that template");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const res = await createNoticeTemplate({
        name: trimmed,
        type: draft.type,
        title: draft.title,
        message: draft.message,
        options: { sms: !!draft.options?.sms, require_acknowledgement: !!draft.options?.require_acknowledgement },
      });
      toast.success(res?.message || "Template saved");
      setSaving(false);
      setName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save the template");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    setBusy(true);
    try {
      await deleteNoticeTemplate(t._id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not delete that template");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rn-templates">
      <div className="rn-templates-row">
        <span className="rn-templates-label">Start from</span>
        {items.length === 0 ? (
          <span className="rn-muted">No saved templates yet. Write a notice and save it for next time.</span>
        ) : (
          items.map((t) => (
            <span key={t._id} className="rn-tpl">
              <button type="button" className="rn-tpl-btn" onClick={() => apply(t)} disabled={busy}>
                {t.name}
                {t.use_count ? <small> ·{t.use_count}</small> : null}
              </button>
              {canDelete ? (
                <button
                  type="button"
                  className="rn-tpl-x"
                  onClick={() => remove(t)}
                  disabled={busy}
                  aria-label="Delete saved template"
                  title={`Delete ${t.name}`}
                >
                  <FeatherIcon icon="x" size={11} />
                </button>
              ) : null}
            </span>
          ))
        )}

        <button type="button" className="rn-tpl-save" onClick={() => setSaving((v) => !v)} disabled={busy || !worthSaving}>
          <FeatherIcon icon="bookmark" size={13} /> Save as template
        </button>
      </div>

      {saving ? (
        <div className="rn-templates-save">
          <input
            type="text"
            value={name}
            placeholder={draft.title || "Template name"}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
          <button type="button" className="rn-btn rn-btn-primary" onClick={save} disabled={busy || !name.trim()}>
            Save
          </button>
          <button type="button" className="rn-btn" onClick={() => setSaving(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TemplatePicker;
